import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The previous / next pair beside a calendar's period pickers — a month on `<Calendar>`, a
 * year on Building Management's year view.
 *
 * One control with two segments, not two buttons that happen to touch — the composition
 * `<SearchGroup>` and `<FieldGroup>` already use: the WRAPPER owns the border and the radius,
 * the segments are borderless, and a `border-l` divides them. Stepping back and stepping forward
 * are one job, and a pair of loose icon buttons beside a pair of dropdowns read as four
 * unrelated things on one row.
 *
 * Quiet at rest — it is chrome beside the label, not an action on the page.
 */
export function PeriodStep({
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
}: {
  previousLabel: string;
  nextLabel: string;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center overflow-hidden rounded-lg border border-slate-300">
      <Step label={previousLabel} onClick={onPrevious}>
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Step>
      <Step label={nextLabel} onClick={onNext} divided>
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Step>
    </div>
  );
}

function Step({
  label,
  onClick,
  divided = false,
  children,
}: {
  label: string;
  onClick: () => void;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={[
        'flex items-center px-2 py-1.5 text-slate-400 transition',
        'hover:bg-slate-100 hover:text-slate-700',
        divided ? 'border-l border-slate-300' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </button>
  );
}
